import styles from '../index.module.css'

import { IoArrowDown } from '@react-icons/all-files/io5/IoArrowDown'
import { IoArrowUp } from '@react-icons/all-files/io5/IoArrowUp'
import { Column, flexRender } from '@tanstack/react-table'
import cn from 'clsx'
import React, { CSSProperties, useRef } from 'react'

import { Flex, Table, Text } from 'components/ds'

import { SearchTableColumnDropdownMenu } from '../table-column-dropdown'
import { sanitizeColumnId } from '../table-utils'

const getHeaderPinningStyles = <T,>(column: Column<T>): CSSProperties => {
  const isPinned = column.getIsPinned()

  return {
    width: `calc(var(--header-${sanitizeColumnId(column.id)}-size) * 1px)`,
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
  }
}
export const TableHeader: React.FC<{
  table
  stickyHeader
  onColumnVisibilityChange
  leafColumns
}> = ({ table, stickyHeader, onColumnVisibilityChange, leafColumns }) => {
  const isResizingRef = useRef(false)

  return (
    <Table.Header className={cn({ [styles.StickyTableHeader]: stickyHeader })}>
      {table.getHeaderGroups().map(headerGroup => (
        <Table.Row key={headerGroup.id}>
          {headerGroup.headers.map(header => (
            <Table.ColumnHeaderCell
              key={header.id}
              align={header.id === 'actions' ? 'right' : undefined}
              style={getHeaderPinningStyles(header.column)}
              width={`calc(var(--header-${sanitizeColumnId(
                header.column.id,
              )}-size) * 1px)`}
              className={cn(styles.HeaderContent, {
                [styles.PinnedCell]: header.column.getIsPinned(),
                [styles.ActionsCell]: header.id === 'actions',
              })}
            >
              <Flex height="100%" justify="between" align="center" width="100%">
                <Flex
                  gap="1"
                  align="center"
                  className={cn({
                    [styles.SortableHeader]:
                      header.column.getCanSort() && !isResizingRef.current,
                  })}
                  onClick={
                    header.column.getCanSort() && !isResizingRef.current
                      ? header.column.getToggleSortingHandler()
                      : undefined
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(
                        header.column.columnDef.header,
                        header.getContext(),
                      )}
                  <Flex>
                    {header.column.getCanSort() &&
                      !header.column.columnDef.meta?.customSortUI &&
                      { asc: <IoArrowDown />, desc: <IoArrowUp /> }[
                        header.column.getIsSorted() as string
                      ]}
                  </Flex>
                </Flex>

                {header.id === 'actions' && onColumnVisibilityChange && (
                  <SearchTableColumnDropdownMenu columns={leafColumns} />
                )}

                <Flex
                  {...(header.column.getCanResize()
                    ? {
                        onDoubleClick: () => header.column.resetSize(),
                        onMouseDown: e => {
                          e.stopPropagation()
                          isResizingRef.current = true
                          header.getResizeHandler()(e)
                        },
                        onTouchStart: e => {
                          e.stopPropagation()
                          isResizingRef.current = true
                          header.getResizeHandler()(e)
                        },
                        onMouseUp: () => {
                          setTimeout(() => {
                            isResizingRef.current = false
                          }, 150)
                        },
                        onTouchEnd: () => {
                          setTimeout(() => {
                            isResizingRef.current = false
                          }, 150)
                        },
                        onMouseLeave: () => {
                          setTimeout(() => {
                            isResizingRef.current = false
                          }, 150)
                        },
                        className: `${cn({
                          [styles.Resizer]: header.column.getCanResize(),
                          [styles.Resizing]: header.column.getIsResizing(),
                        })}`,
                      }
                    : {})}
                />
              </Flex>
            </Table.ColumnHeaderCell>
          ))}
        </Table.Row>
      ))}
    </Table.Header>
  )
}
